import { BOOKER_PHONE_QUESTION_ID_CHANGA, LOCATION_QUESTION_ID } from '@/constants/constants';
import IConnectedCalendar from '@/types/IConnectedCalendar';
import IEventType, { IEventTypeLocation } from '@/types/IEventType';
import IQuestion from '@/types/IQuestion';
import { ICalendarProvider } from '@/types/IThirdPartyCalendar';
import { ILocationOption } from './useSectionGeneral';

export function checkIfThereAreConnectedCalendars(service: IEventType, provider: ICalendarProvider) {
	return service.hosts.some((host) =>
		host.calendars.some((calendar: IConnectedCalendar) => calendar.provider === provider && calendar.syncBookings)
	);
}

export function computeLocation({
	location,
	locationMeta,
}: {
	location: ILocationOption;
	locationMeta: string;
}): IEventTypeLocation {
	switch (location) {
		case 'GOOGLE_MEET':
			return { type: 'GoogleMeetLocation' };
		case 'MS_TEAMS':
			return { type: 'MicrosoftOutlookLocation' };
		case 'BOOKER_PHONE':
			return { type: 'BookerPhoneLocation', questionId: BOOKER_PHONE_QUESTION_ID_CHANGA };
		case 'FIXED':
			return { type: 'FixedLocation', freeText: locationMeta };
		case 'BOOKER_SELECTION':
			return { type: 'BookerSelectionLocation', questionId: LOCATION_QUESTION_ID };
	}
}

export function parseLocation(location?: IEventTypeLocation): ILocationOption {
	if (!location) {
		console.warn('This event type has no location. Fallback to fixed');
		return 'FIXED';
	}

	switch (location.type) {
		case 'GoogleMeetLocation':
			return 'GOOGLE_MEET';
		case 'MicrosoftOutlookLocation':
			return 'MS_TEAMS';
		case 'BookerPhoneLocation':
			return 'BOOKER_PHONE';
		case 'FixedLocation':
			return 'FIXED';
		case 'BookerSelectionLocation':
			return 'BOOKER_SELECTION';
	}
}

export function parseLocationMeta(location?: IEventTypeLocation): string {
	if (location?.type === 'FixedLocation') {
		return location.freeText;
	}
	return '';
}

/**
 * The user location question is not required when the location is not booker_selection.
 * TODO: The user phone question is not required when the location is not booker_phone
 * @returns
 */
export function computeQuestions(type: ILocationOption, eventType: IEventType): IQuestion[] {
	/**
	 * Booker selection --> Add location question if not exist and remove phone question
	 */
	if (type === 'BOOKER_SELECTION') {
		if (!eventType.questions.some((q) => q.id === LOCATION_QUESTION_ID)) {
			eventType.questions.push({
				id: LOCATION_QUESTION_ID,
				label: 'Location',
				required: true,
				type: 'LOCATION',
				allowedOptions: [],
			});
		}

		return eventType.questions.filter((q) => q.id !== BOOKER_PHONE_QUESTION_ID_CHANGA);
	}

	/**
	 * Booker phone --> Add phone question if not exist and remove location question
	 */
	if (type === 'BOOKER_PHONE') {
		if (!eventType.questions.some((q) => q.id === BOOKER_PHONE_QUESTION_ID_CHANGA)) {
			eventType.questions.push({
				id: BOOKER_PHONE_QUESTION_ID_CHANGA,
				label: 'Phone',
				required: true,
				type: 'PHONE',
			});
		}
		return eventType.questions.filter((q) => q.id !== LOCATION_QUESTION_ID);
	}

	// Here is not booker phone nor booker selection. filter both
	return eventType.questions
		.filter((q) => q.id !== LOCATION_QUESTION_ID)
		.filter((q) => q.id !== BOOKER_PHONE_QUESTION_ID_CHANGA);
}

export function computeTags(tags: IEventType['tags'], shouldAutoComputeStep: boolean): IEventType['tags'] {
	return { ...tags, ttStepMode: shouldAutoComputeStep ? 'auto' : 'manual' };
}
